// src/components/Dashboard/StatusToken.tsx
//
// One shape for a status token across both Outsourcing dashboards (EE-238).
//
// The two pages rendered the same semantic token three different ways: the
// Requisition row used a MUI Chip (pill, 16px radius), the Inquiry row used
// the Inquiry row's own status Button (5px radius + caret), and the shared filter
// chips used a Button at MUI's default 4px. On the Requisition page the same
// status string appeared as a pill in the row and a rectangle in the filter
// bar at the same time.
//
// Everything now renders at STATUS_TOKEN_RADIUS. Colours still come from each
// page's own status map - only the shape is shared.
//
// The token also pins its own text-transform. The Inquiry row wraps it in a
// MUI Button (it doubles as the status dropdown's hit target) whose default
// uppercase leaked in, so the same component rendered SENT / AWAITING RESPONSE
// on one dashboard and Submitted on the other - visible side by side when you
// switch tabs. A row reports a fact and reads in Title case; the filter chips
// are controls and stay uppercase, which is what MUI gives them anyway.
import { FunctionComponent, ReactNode } from "react";
import { Box } from "@mui/material";
import { StatusColors } from "./StatusFilterChips";

export const STATUS_TOKEN_RADIUS = "var(--statusTokenRadius)";

type PropsType = {
  label: string;
  colors: StatusColors;
  // Rendered inside the token, after the label - used by the Inquiry row's
  // editable variant for its dropdown caret.
  adornment?: ReactNode;
};

const StatusToken: FunctionComponent<PropsType> = ({
  label,
  colors,
  adornment,
}) => (
  <Box
    component="span"
    data-testid="status-token"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "0.25rem",
      padding: "4px 10px",
      fontSize: "0.8125rem",
      fontWeight: "bold",
      lineHeight: 1.75,
      whiteSpace: "nowrap",
      textTransform: "none",
      letterSpacing: 0,
      borderRadius: STATUS_TOKEN_RADIUS,
      border: colors?.border,
      color: colors?.color,
      background: colors?.background,
    }}
  >
    {label}
    {adornment}
  </Box>
);

export default StatusToken;
